import { Dialog, Transition } from '@headlessui/react'
import React, { Fragment, useCallback, useRef, useState } from 'react'
import StandaloneCheckbox from '../../components/Form/StandaloneCheckbox'
import { graphql } from '../../gql'
import { useMutation } from 'urql'

interface RetireParcelModalProps {
  parcelId: string | null
  isOpen: boolean
  onClose: () => void
  onParcelDeleted?: () => void
}

type ParcelRetireReason = 'worn-out' | 'zipper-broken' | 'package-discolored'

const RetireParcelMutation = graphql(`
  mutation RetireParcel($id: ID!, $reasons: [String!]!) {
    retireBag(id: $id, reasons: $reasons) {
      bag {
        id
        retiredAt
        retiredReasons
      }
    }
  }
`)

export const RetireParcelModal: React.FC<RetireParcelModalProps> = ({
  parcelId,
  isOpen,
  onClose,
  onParcelDeleted,
}: RetireParcelModalProps) => {
  const cancelButtonRef = useRef(null)

  const [parcelRetireReasons, setParcelRetireReasons] = useState<
    ParcelRetireReason[]
  >([])

  const [, retireParcelMutation] = useMutation(RetireParcelMutation)

  const handleRetireParcel = useCallback(() => {
    if (!parcelId) {
      return
    }

    retireParcelMutation({
      id: parcelId,
      reasons: parcelRetireReasons,
    }).then(() => {
      if (onParcelDeleted) {
        onParcelDeleted()
      }
    })
  }, [retireParcelMutation, parcelId, parcelRetireReasons, onParcelDeleted])

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-10"
        initialFocus={cancelButtonRef}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      Retire parcel
                    </Dialog.Title>
                    <div className="mt-4">
                      <p className="text-sm text-gray-900 font-medium leading-5">
                        Select reason to retire parcel
                      </p>
                      <fieldset className={'mt-4 flex flex-col gap-4'}>
                        <StandaloneCheckbox
                          id={'worn-out'}
                          label={'The parcel is worn out'}
                          checked={parcelRetireReasons.includes('worn-out')}
                          onChange={checked => {
                            if (checked) {
                              setParcelRetireReasons([
                                ...parcelRetireReasons,
                                'worn-out',
                              ])
                            } else {
                              setParcelRetireReasons(
                                parcelRetireReasons.filter(
                                  reason => reason !== 'worn-out'
                                )
                              )
                            }
                          }}
                        />
                        <StandaloneCheckbox
                          id={'zipper-broken'}
                          label={'The zipper is broken'}
                          checked={parcelRetireReasons.includes(
                            'zipper-broken'
                          )}
                          onChange={() => {
                            if (parcelRetireReasons.includes('zipper-broken')) {
                              setParcelRetireReasons(
                                parcelRetireReasons.filter(
                                  reason => reason !== 'zipper-broken'
                                )
                              )
                            } else {
                              setParcelRetireReasons([
                                ...parcelRetireReasons,
                                'zipper-broken',
                              ])
                            }
                          }}
                        />
                        <StandaloneCheckbox
                          id={'package-discolored'}
                          label={'The package is discolored'}
                          checked={parcelRetireReasons.includes(
                            'package-discolored'
                          )}
                          onChange={() => {
                            if (
                              parcelRetireReasons.includes('package-discolored')
                            ) {
                              setParcelRetireReasons(
                                parcelRetireReasons.filter(
                                  reason => reason !== 'package-discolored'
                                )
                              )
                            } else {
                              setParcelRetireReasons([
                                ...parcelRetireReasons,
                                'package-discolored',
                              ])
                            }
                          }}
                        />
                      </fieldset>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-8 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    className="inline-flex w-full justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={handleRetireParcel}
                  >
                    Retire parcel
                  </button>
                  <button
                    type="button"
                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
                    onClick={onClose}
                    ref={cancelButtonRef}
                  >
                    Cancel
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
